import { router } from "@inertiajs/react"
import { Modals, useModals } from "@/hooks/useModals"
import Modal from "@/components/Modal"
import { useMemo, useState } from "react"
import { compact, defaults, find, map, reject, union, values } from "lodash-es"
import { parseGames } from "@mliebelt/pgn-parser"
import { Chess, Move } from "chess.js"
import GameUpload from "@/components/GameUpload"
import GameUploadSelect from "@/components/GameUploadSelect"
import GameUploadMatch from "@/components/GameUploadMatch"
import GameUploadLoader, { UploadResult } from "@/components/GameUploadLoader"
import { startFen } from "@/constants"
import { useQueryClient } from "@tanstack/react-query"
import formatDate from "@/utils/formatDate"
import usePlayers from "@/hooks/usePlayers"
import useEvents from "@/hooks/useEvents"
import axios from "axios"
import { captureEvent } from "@/lib/posthog"

export type UploadStatus =
  | "waiting"
  | "processing"
  | "uploading"
  | "finished"
  | "error"

type MoveItem = Move & {
  comment: string
  annotation?: "!" | "?" | "!!" | "??" | "!?" | "?!"
  moves: Array<Array<MoveItem>>
}

const NAG_TO_SYMBOL: Record<string, MoveItem["annotation"]> = {
  $1: "!",
  $2: "?",
  $3: "!!",
  $4: "??",
  $5: "!?",
  $6: "?!",
}

function extractComment(node: any): string {
  const parts = [
    node?.commentBefore?.comment,
    node?.commentMove?.comment,
    node?.commentAfter?.comment,
    node?.commentDiag?.comment,
  ].filter(Boolean)
  return parts.join(" ").trim()
}

function extractAnnotation(node: any): MoveItem["annotation"] | undefined {
  const suffix = node?.notation?.suffixAnnotation // "!", "?!", etc.
  if (suffix) return suffix as MoveItem["annotation"]

  const nag: string | undefined =
    node?.notation?.nag ||
    node?.nag ||
    (Array.isArray(node?.glyphs) && node.glyphs[0]?.id
      ? `$${node.glyphs[0].id}`
      : undefined)

  return nag && NAG_TO_SYMBOL[nag] ? NAG_TO_SYMBOL[nag] : undefined
}

const remapMoves = (
  lines: any[],
  fen: string | undefined,
  isChess960 = false
): Array<Array<MoveItem>> => {
  if (!Array.isArray(lines) || !lines.length) return []

  const out: Array<Array<MoveItem>> = []

  for (const line of lines) {
    if (!Array.isArray(line) || !line.length) continue

    // Start a fresh Chess.js instance from the given FEN (or standard start)
    const game = new Chess(fen, { chess960: isChess960 })
    const currLine: Array<MoveItem> = []
    const extraLines: Array<Array<MoveItem>> = []

    let prevItem: MoveItem | null = null

    for (const ply of line) {
      //  Parse a single SAN move
      const san: string | undefined = ply?.notation?.notation
      if (!san) continue

      const fenBefore = game.fen()
      const pushed = game.move(san)
      if (!pushed) {
        throw new Error(`Invalid move: ${san} (from FEN=${fenBefore})`)
      }
      const fenAfter = game.fen()

      // Create our MoveItem (comment + annotation + base move info)
      const verbose = game.history({ verbose: true })
      const last = verbose[verbose.length - 1] as Move
      const item: MoveItem = {
        ...(last as any),
        comment: extractComment(ply),
        annotation: extractAnnotation(ply),
        moves: [],
      }
      currLine.push(item)

      //  Handle variations branching off this move
      const variations: any[][] = ply?.variations || []

      for (const alt of variations) {
        if (!Array.isArray(alt) || !alt.length) continue
        const firstSan: string | undefined = alt[0]?.notation?.notation

        // Determine whether this variation belongs on the current move
        // or should be shifted to the previous move (DB vs PGN mismatch).
        const altTurn: "w" | "b" | undefined = alt[0]?.turn

        const turnBefore = fenBefore.split(" ")[1] as "w" | "b"
        const turnAfter = fenAfter.split(" ")[1] as "w" | "b"

        let startFen = fenAfter
        let attachesToPrev = false

        if (altTurn) {
          if (altTurn === turnBefore) {
            startFen = fenBefore
            attachesToPrev = true
          } else if (altTurn === turnAfter) {
            startFen = fenAfter
            attachesToPrev = false
          } else {
            // Fallback: try to see which FEN actually accepts the move
            if (firstSan) {
              const okAfter = !!new Chess(fenAfter, { chess960: isChess960 }).move(firstSan)
              const okBefore = !!new Chess(fenBefore, { chess960: isChess960 }).move(firstSan)
              if (!okAfter && okBefore) {
                startFen = fenBefore
                attachesToPrev = true
              }
            }
          }
        } else {
          // Same fallback logic if no explicit altTurn
          if (firstSan) {
            const okAfter = !!new Chess(fenAfter, { chess960: isChess960 }).move(firstSan)
            const okBefore = !!new Chess(fenBefore, { chess960: isChess960 }).move(firstSan)
            if (!okAfter && okBefore) {
              startFen = fenBefore
              attachesToPrev = true
            }
          }
        }

        // Recursively process the variation line
        const nested = remapMoves([alt], startFen, isChess960)

        // Attach to either the previous white move or the current move
        if (attachesToPrev) {
          if (prevItem) prevItem.moves.push(...nested)
          else extraLines.push(...nested)
        } else {
          item.moves.push(...nested)
        }
      }

      prevItem = item
    }

    // Push the main line and any extra orphaned variation lines
    out.push(currLine)
    if (extraLines.length) out.push(...extraLines)
  }

  return out
}

const processGames = (games: any[]) => {
  const getValue = (value: string) =>
    value !== "?" && value !== "????.??.??" ? value : null

  return compact(
    games.map(({ tags, moves }) => {
      const isChess960 =
        tags.Variant?.toLowerCase() === "chess960" ||
        tags.Variant?.toLowerCase() === "fischerandom"

      return {
        start: tags.FEN ?? startFen,
        chess960: isChess960,
        date: getValue(tags.Date) ? formatDate({ date: tags.Date.value }) : null,
        result: getValue(tags.Result) || "*",
        event: {
          name: getValue(tags.Event),
          date: getValue(tags.EventDate)
            ? formatDate({ date: tags.EventDate.value })
            : null,
          site: getValue(tags.Site),
        },
        round: getValue(tags.Round),
        white: {
          name: getValue(tags.White),
          title: getValue(tags.WhiteTitle),
          elo: getValue(tags.WhiteElo),
          fideId: getValue(tags.WhiteFideId),
        },
        black: {
          name: getValue(tags.Black),
          title: getValue(tags.BlackTitle),
          elo: getValue(tags.BlackElo),
          fideId: getValue(tags.BlackFideId),
        },
        opening: {
          code: getValue(tags.ECO),
          name: getValue(tags.Opening),
        },
        moves: remapMoves([moves], tags.FEN, isChess960),
      }
    })
  )
}

const ImportModal = () => {
  const { activeModal, closeModal } = useModals()
  const isOpen = activeModal === Modals.ImportGames
  const queryClient = useQueryClient()

  const { players } = usePlayers()
  const { events } = useEvents()

  const [step, setStep] = useState(0)
  const [games, setGames] = useState<any[]>([])
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([])
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([])

  const selectedGames = useMemo(
    () =>
      selectedIndexes
        .map((index) => games[index])
        .filter((obj) => obj !== undefined),
    [selectedIndexes, games]
  )

  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("waiting")

  const [eventMapping, setEventMapping] = useState<
    Record<string, string | undefined>
  >({})
  const [playerMapping, setPlayerMapping] = useState<
    Record<string, string | undefined>
  >({})

  const handleFileChange = async (pgnText: string) => {
    setUploadStatus("processing")

    await new Promise((r) => setTimeout(r, 10))

    try {
      const parsedGames = parseGames(pgnText, { startRule: "games" })

      if (!Array.isArray(parsedGames)) {
        throw new Error("Invalid PGN file.")
      }

      if (parsedGames.length > 100) {
        throw new Error(
          "The file contains more than 100 games. Please split it into multiple files."
        )
      }

      const games = processGames(parsedGames)

      setGames(games)

      if (!games.length) {
        throw new Error("No games found in the file.")
      } else if (games.length === 1) {
        // If only one game in the file, select it automatically
        setSelectedIndexes([0])
        setStep(1)
      } else {
        setStep(1)
      }
    } catch (error: any) {
      alert(error?.message || "Something went wrong. Please try again.")
      setUploadStatus("waiting")
    }
  }

  const handleGameSelect = (index: number) => {
    if (selectedIndexes.includes(index)) {
      setSelectedIndexes(selectedIndexes.filter((item) => item !== index))
    } else {
      setSelectedIndexes([...selectedIndexes, index])
    }
  }

  const handleSelectAllGames = () => {
    if (selectedIndexes.length < games.length) {
      const allGames = Array.from({ length: games.length }, (_, index) => index)
      setSelectedIndexes(allGames)
    } else {
      setSelectedIndexes([])
    }
  }

  const mergeData = (objects: any) => {
    objects = reject(
      objects,
      (obj) => obj.name === undefined || obj.name === ""
    )

    const unique: Record<string, any> = {}
    objects.forEach((obj: any) => {
      unique[obj.name] = defaults(unique[obj.name] || {}, obj)
    })

    return values(unique)
  }

  const newEvents = useMemo(
    () => mergeData(map(selectedGames, "event")),
    [selectedGames]
  )

  const newPlayers = useMemo(() => {
    const whites = map(selectedGames, "white")
    const blacks = map(selectedGames, "black")
    return mergeData(union(whites, blacks))
  }, [selectedGames])

  const handleMatchChange = (
    type: "event" | "player",
    match: {
      source: string
      target: string | undefined
    }
  ) => {
    if (type === "player") {
      const newPlayerMapping = { ...playerMapping }
      newPlayerMapping[match.source] = match.target
      setPlayerMapping(newPlayerMapping)
    }

    if (type === "event") {
      const newEventMapping = { ...eventMapping }
      newEventMapping[match.source] = match.target
      setEventMapping(newEventMapping)
    }
  }

  const importGames = async () => {
    try {
      setUploadStatus("uploading")
      setUploadResults([])

      const games = selectedGames.map((game) => {
        if (eventMapping[game.event.name])
          game.event.name = eventMapping[game.event.name]
        if (playerMapping[game.white.name])
          game.white.name = playerMapping[game.white.name]
        if (playerMapping[game.black.name])
          game.black.name = playerMapping[game.black.name]
        return game
      })

      const { data } = await axios.post<UploadResult[]>("/api/games", {
        games,
      })

      router.reload()

      setUploadStatus("finished")
      setUploadResults(data)

      captureEvent("games_imported", { game_count: data.length })

      queryClient.invalidateQueries({ queryKey: ["events"] })
      queryClient.invalidateQueries({ queryKey: ["players"] })
    } catch (error: any) {
      console.log(error)

      setUploadStatus("error")
      alert(
        error?.response?.data?.error ||
          "Something went wrong. Please try again."
      )
    }
  }

  const goToNextStep = () => {
    // Match the selected events and players
    if (step === 1) {
      if (newPlayers.length === 0 && newEvents.length === 0) {
        // Skip the matching step and go straight to import
        return importGames()
      }

      const newEventMapping = { ...eventMapping }
      newEvents.forEach((event) => {
        const { name } = event
        const match = find(events, (e) => e.name === name)
        newEventMapping[name] = (match || {}).name
      })

      setEventMapping(newEventMapping)

      const newPlayerMapping = { ...playerMapping }
      newPlayers.forEach((player) => {
        const { name } = player
        const match = find(players, (p) => p.name === name)
        newPlayerMapping[name] = (match || {}).name
      })

      setPlayerMapping(newPlayerMapping)
    }

    setStep((prev) => prev + 1)
  }

  const resetModal = () => {
    setGames([])
    setStep(0)
    setSelectedIndexes([])
    setUploadStatus("waiting")
    setUploadResults([])
  }

  const handleClose = () => {
    resetModal()
    closeModal()
  }

  const modalTitle =
    uploadStatus === "finished"
      ? "Done!"
      : step === 1
        ? "Select games"
        : step === 2
          ? "Match games"
          : "Import games"

  return (
    <Modal
      show={isOpen}
      onClose={handleClose}
      title={modalTitle}
      maxWidthClassName="sm:max-w-xl"
    >
      {["waiting", "processing"].includes(uploadStatus) ? (
        <>
          {step === 0 && (
            <GameUpload
              onFileChange={handleFileChange}
              isProcessing={uploadStatus === "processing"}
            />
          )}
          {step === 1 && (
            <GameUploadSelect
              games={games}
              selectedIndexes={selectedIndexes}
              selectGame={handleGameSelect}
              selectAllGames={handleSelectAllGames}
              next={goToNextStep}
              previous={resetModal}
            />
          )}
          {step === 2 && (
            <GameUploadMatch
              players={players}
              events={events}
              newEvents={newEvents}
              newPlayers={newPlayers}
              eventMapping={eventMapping}
              playerMapping={playerMapping}
              onMatch={handleMatchChange}
              onNext={importGames}
              previous={() => setStep(1)}
            />
          )}
        </>
      ) : (
        <GameUploadLoader
          status={uploadStatus}
          setStatus={setUploadStatus}
          tryAgain={importGames}
          done={() => handleClose()}
          uploadResults={uploadResults}
        />
      )}
    </Modal>
  )
}

export default ImportModal
